import { useState, useEffect } from 'react'
import Front from '@frontapp/plugin-sdk'
import {
  FrontContext,
  FrontConversation,
  UseFrontContextReturn
} from '@/types/front'

export const useFrontContext = (): UseFrontContextReturn => {
  const [context, setContext] = useState<FrontContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let mounted = true
    let frontFired = false

    // Dev-only standalone bypass: when VITE_DEV_LOAD_NUMBER is set and Front
    // SDK never fires (running at localhost outside Front), inject a synthetic
    // conversation so the plugin renders. Strip in staging/prod by leaving the
    // var unset in those env files.
    const devLoadNumber = import.meta.env.VITE_DEV_LOAD_NUMBER as
      | string
      | undefined

    const syntheticContext = (loadNumber: string): FrontContext =>
      ({
        type: 'singleConversation',
        conversation: {
          id: `dev-${loadNumber}`,
          subject: `Dev preview — Load ${loadNumber}`,
          tags: [{ name: `load:${loadNumber}` }]
        }
      }) as unknown as FrontContext

    const showSubscribeFailure = (err: unknown) => {
      if (devLoadNumber) {
        setContext(syntheticContext(devLoadNumber))
        setLoading(false)
      } else {
        setError(err as Error)
        setLoading(false)
      }
    }

    let subscription: { unsubscribe: () => void } | null = null
    let devTimer: ReturnType<typeof setTimeout> | undefined

    try {
      subscription = Front.contextUpdates.subscribe(frontContext => {
        if (mounted) {
          frontFired = true
          setContext(frontContext as FrontContext)
          setLoading(false)
        }
      })

      if (devLoadNumber) {
        devTimer = setTimeout(() => {
          if (mounted && !frontFired) {
            setContext(syntheticContext(devLoadNumber))
            setLoading(false)
          }
        }, 400)
      }
    } catch (err) {
      showSubscribeFailure(err)
    }

    return () => {
      mounted = false
      clearTimeout(devTimer)
      subscription?.unsubscribe()
    }
  }, [])

  // Extract single conversation from context
  const conversation: FrontConversation | null =
    context?.type === 'singleConversation' ? context.conversation || null : null

  return {
    context,
    conversation,
    loading,
    error
  }
}

export default useFrontContext
